import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    Grid,
    Collapse,
    Stepper,
    Step,
    StepLabel,
    StepContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FormField from './FormField';

const ExpandableCategory = ({
    title,
    fields,
    values,
    setValues,
    columns,
    onRemove,
    categoryId,
    expanded,
    onToggleExpand,
    savedSections,
    onSaveSections
}) => {
    const [sections, setSections] = useState(() => {
        // Use saved sections if available, otherwise initialize new
        if (savedSections && savedSections.length > 0) {
            return savedSections;
        }

        // Initialize with existing data if available
        const existingSections = [];
        const prefix = `${categoryId}-`;

        Object.keys(values).forEach(key => {
            if (key.startsWith(prefix)) {
                const sectionId = key.split('-')[1];
                if (!existingSections.find(s => s.id === sectionId)) {
                    existingSections.push({
                        id: sectionId,
                        activeStep: 0
                    });
                }
            }
        });

        return existingSections.length > 0 ? existingSections : [{ id: Date.now(), activeStep: 0 }];
    });


    useEffect(() => {
      const newSections = savedSections && savedSections.length > 0
        ? savedSections
        : (() => {
            const existingSections = [];
            const prefix = `${categoryId}-`;

            Object.keys(values).forEach(key => {
              if (key.startsWith(prefix)) {
                const sectionId = key.split('-')[1];
                if (!existingSections.find(s => s.id === sectionId)) {
                  existingSections.push({
                    id: sectionId,
                    activeStep: 0
                  });
                }
              }
            });

            return existingSections.length > 0 ? existingSections : [{ id: Date.now(), activeStep: 0 }];
          })();

      if (JSON.stringify(newSections) !== JSON.stringify(sections)) {
        setSections(newSections);
        if (onSaveSections) {
          onSaveSections(newSections);
        }
      }
    }, [categoryId, values, savedSections, onSaveSections]);

    const steps = [
        'Basic Details',
        'Date Range',
        'Content',
        'Images'
    ];


    const addSection = useCallback(() => {
      const newSections = [...sections, { id: Date.now(), activeStep: 0 }];
      setSections(newSections);
      if (onSaveSections) {
        onSaveSections(newSections);
      }
    }, [sections, setSections, onSaveSections]);

    const removeSection = useCallback((sectionId) => {
      const newSections = sections.filter(section => section.id !== sectionId);
      setSections(newSections);
      if (onSaveSections) {
        onSaveSections(newSections);
      }

      // Clean up values for this section
      const newValues = { ...values };
      Object.keys(newValues).forEach(key => {
        if (key.includes(`${categoryId}-${sectionId}`)) {
          delete newValues[key];
        }
      });
      setValues(newValues);
    }, [categoryId, setValues, values, setSections, sections, onSaveSections]);

    const handleNext = useCallback((sectionId) => {
      const newSections = sections.map(section =>
        section.id === sectionId
          ? { ...section, activeStep: Math.min(section.activeStep + 1, steps.length - 1) }
          : section
      );
      setSections(newSections);
      if (onSaveSections) {
        onSaveSections(newSections);
      }
    }, [setSections, sections, steps.length, onSaveSections]);

    const handleBack = useCallback((sectionId) => {
      const newSections = sections.map(section =>
        section.id === sectionId
          ? { ...section, activeStep: Math.max(section.activeStep - 1, 0) }
          : section
      );
      setSections(newSections);
      if (onSaveSections) {
        onSaveSections(newSections);
      }
    }, [setSections, sections, onSaveSections]);

    const handleFinish = useCallback((sectionId) => {
      const newSections = sections.map(section =>
        section.id === sectionId
          ? { ...section, activeStep: steps.length }
          : section
      );
      setSections(newSections);
      if (onSaveSections) {
        onSaveSections(newSections);
      }
    }, [setSections, sections, steps.length, onSaveSections]);

   

    const getStepFields = (step) => {
        switch (step) {
            case 0:
                return fields.filter(field =>
                    !field.includes('Day') &&
                    !field.includes('input_text') &&
                    !field.includes('picture') &&
                    !field.includes('subject_line') &&
                    !field.includes('feed_show_status')
                );
            case 1:
                return fields.filter(field => field.includes('Day'));
            case 2:
                return fields.filter(field =>
                    field.includes('input_text') ||
                    field.includes('subject_line') ||
                    field.includes('feed_show_status')
                );
            case 3:
                return fields.filter(field => field.includes('picture'));
            default:
                return [];
        }
    };

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" color="primary">
                        {title}
                    </Typography>
                    <Box>
                        <IconButton onClick={addSection} size="small" title="Add Section">
                            <AddIcon />
                        </IconButton>
                        <IconButton onClick={onToggleExpand} size="small">
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <IconButton onClick={onRemove} size="small" color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Collapse in={expanded}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ display: 'flex', gap: 4, pb: 2 }}>
                            {sections.map((section) => (
                                <Box
                                    key={section.id}
                                    sx={{
                                        minWidth: 300,
                                        position: 'relative',
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1
                                    }}
                                >
                                    <IconButton
                                        onClick={() => removeSection(section.id)}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 1
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                    <Stepper activeStep={section.activeStep} orientation="vertical">
                                        {steps.map((label, index) => (
                                            <Step key={`${section.id}-${label}`}>
                                                <StepLabel>{label}</StepLabel>
                                                <StepContent>
                                                    <Grid container spacing={2}>
                                                        {getStepFields(index).map((fieldName) => {
                                                            const field = columns.find(col => col.field === fieldName);
                                                            if (!field) return null;

                                                            if (field.inputType === 'heading') {
                                                                return (
                                                                    <Grid item xs={12} key={`${fieldName}-${section.id}`}>
                                                                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                                                            {field.title}
                                                                        </Typography>
                                                                    </Grid>
                                                                );
                                                            }

                                                            return (
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    key={`${fieldName}-${section.id}`}
                                                                >
                                                                    <FormField
                                                                        structure={{
                                                                            ...field,
                                                                            field: `${categoryId}-${section.id}-${field.field}`
                                                                        }}
                                                                        id={`section-${section.id}`}
                                                                        values={values}
                                                                        setValues={setValues}
                                                                        showPaypalFields={values.paypalMode}
                                                                    />
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                    <Box sx={{ mt: 2 }}>
                                                        <Button
                                                            disabled={index === 0}
                                                            onClick={() => handleBack(section.id)}
                                                            sx={{ mr: 1 }}
                                                            variant="outlined"
                                                            size="small"
                                                        >
                                                            Back
                                                        </Button>
                                                        {index === steps.length - 1 ? (
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => handleFinish(section.id)}
                                                                size="small"
                                                                color="primary"
                                                            >
                                                                Finish
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="contained"
                                                                onClick={() => handleNext(section.id)}
                                                                size="small"
                                                            >
                                                                Continue
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </StepContent>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default React.memo(ExpandableCategory);

